import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import Switch from 'react-switch';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import * as accessStyles from '../office-access.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import { isValidEmail } from '../../../core/utils/email-utils';
import {
  setPermittedUserDetails,
  setPermittedUserName,
  type PermittedUserDetails,
  type PermittedUserDetailsChange,
  type PermittedUserName,
} from '../services/users.services';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { isValidPhoneNumber } from '../utils/phone-number';
import { detailsErrorMessage } from '../utils/details-error';
import ConfirmMedicChangeModal from './confirm-medic-change-modal.component';

interface Props {
  readonly showModal: boolean;
  /** `OfficeUser.Id` of the person being edited — how both saves are addressed. */
  readonly officeUserId?: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly sharedEmail: string;
  readonly phoneNumber: string;
  readonly isCompanyMedic: boolean;
  readonly handleClose: () => void;
  /**
   * Called with what the server stored, never with what was typed. Either part is undefined when
   * that part was not changed and so not sent.
   */
  readonly handleSaved: (savedName?: PermittedUserName, savedDetails?: PermittedUserDetails, keepOpen?: boolean) => void;
}

/**
 * Edits what is held for a colleague: the name, which is **one record shared by every office**,
 * and the shared email, phone number and company medic flag, which belong to **this office only**.
 *
 * The two halves go to two endpoints because they live in two places — and each is only sent when
 * it changed, so correcting a phone number does not also push an unchanged name to Keycloak.
 */
const EditPermittedUserModal = ({
  showModal,
  officeUserId,
  email,
  firstName,
  lastName,
  sharedEmail,
  phoneNumber,
  isCompanyMedic,
  handleClose,
  handleSaved,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [shared, setShared] = useState(sharedEmail);
  const [phone, setPhone] = useState(phoneNumber);
  const [medic, setMedic] = useState(isCompanyMedic);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  // What the server holds for the details, which is what "changed" is measured against. Starts as
  // the row, and moves when a save stores them — so when the name then fails, retrying sends only
  // the name rather than the details again, and does not ask the medic question a second time.
  const [savedShared, setSavedShared] = useState(sharedEmail);
  const [savedPhone, setSavedPhone] = useState(phoneNumber);
  const [savedMedic, setSavedMedic] = useState(isCompanyMedic);
  // Flipping the switch alone asks nothing; saving it does, the same question the listing asks.
  const [confirmingMedic, setConfirmingMedic] = useState(false);

  // Seeded from the row each time the dialog opens, not once at mount: the component stays mounted
  // between openings, so without this the second person edited would open holding the first one's
  // values — and the administrator would save them onto them.
  useEffect(() => {
    if (showModal) {
      setFirst(firstName);
      setLast(lastName);
      setShared(sharedEmail);
      setPhone(phoneNumber);
      setMedic(isCompanyMedic);
      setSavedShared(sharedEmail);
      setSavedPhone(phoneNumber);
      setSavedMedic(isCompanyMedic);
      setFailure(undefined);
      setSaving(false);
      setConfirmingMedic(false);
    }
  }, [showModal, officeUserId, firstName, lastName, sharedEmail, phoneNumber, isCompanyMedic]);

  const nameChanged = first.trim() !== firstName.trim() || last.trim() !== lastName.trim();
  const sharedChanged = shared.trim() !== savedShared.trim();
  const phoneChanged = phone.trim() !== savedPhone.trim();
  const medicChanged = medic !== savedMedic;
  const detailsChanged = sharedChanged || phoneChanged || medicChanged;

  // Blank is fine for both — it clears them. Only a value that is present and malformed is refused.
  const sharedInvalid = shared.trim() !== '' && !isValidEmail(shared);
  const phoneInvalid = phone.trim() !== '' && !isValidPhoneNumber(phone);

  const save = async (): Promise<void> => {
    if (officeUserId == null) return;

    setSaving(true);
    setFailure(undefined);

    let savedDetails: PermittedUserDetails | undefined;
    let savedName: PermittedUserName | undefined;

    // Details first: they are the half that can be refused for its content, and a refusal there
    // should not leave the name already changed behind the administrator's back.
    if (detailsChanged) {
      const change: PermittedUserDetailsChange = {
        sharedEmail: sharedChanged ? shared.trim() : undefined,
        phoneNumber: phoneChanged ? phone.trim() : undefined,
        isCompanyMedic: medicChanged ? medic : undefined,
      };

      const outcome = await setPermittedUserDetails(officeUserId, change, keycloak);

      if (outcome.saved === undefined) {
        setSaving(false);
        setFailure(detailsErrorMessage(outcome, (key) => t(key).toString()));
        return;
      }

      savedDetails = outcome.saved;
      setSavedShared(outcome.saved.sharedEmail ?? '');
      setSavedPhone(outcome.saved.phoneNumber ?? '');
      setSavedMedic(outcome.saved.isCompanyMedic);
    }

    if (nameChanged) {
      const outcome = await setPermittedUserName(officeUserId, first, last, keycloak);

      if (outcome.saved === undefined) {
        setSaving(false);
        // The details are already stored, so hand them back before reporting — otherwise the row
        // would keep showing the old ones and invite the same change again.
        if (savedDetails) handleSaved(undefined, savedDetails, true);
        setFailure(t('edit-name-failed').toString());
        return;
      }

      savedName = outcome.saved;
    }

    setSaving(false);
    handleSaved(savedName, savedDetails);
  };

  const runSave = (): void => {
    save().catch(() => {
      setSaving(false);
      setFailure(t('edit-user-failed').toString());
    });
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>{t('edit-user-title', { email })}</span>

        {/* Said before the change, not after. Somebody correcting a typo in one office has no
            reason to expect it to reach the other seven, and the sentence costs nothing. */}
        <span className={style(styles.bodyText)}>{t('edit-name-is-global')}</span>

        {/* Labelled rather than placeholder-only: a placeholder disappears the moment there is a
            value, so an administrator correcting one of two adjacent fields would be left with
            unlabelled boxes and no way to tell which is which. */}
        <div className={style(accessStyles.modalForm)}>
          <label className={style(accessStyles.modalField)} htmlFor="edit-permitted-user-first-name">
            <span className={style(accessStyles.modalFieldLabel)}>{t('first-name')}</span>
            <input
              id="edit-permitted-user-first-name"
              className={style(surfaceStyles.textInput, accessStyles.modalTextInput)}
              type="text"
              placeholder={t('first-name-optional').toString()}
              value={first}
              disabled={saving}
              onChange={(event): void => setFirst(event.target.value)}
            />
          </label>

          <label className={style(accessStyles.modalField)} htmlFor="edit-permitted-user-last-name">
            <span className={style(accessStyles.modalFieldLabel)}>{t('last-name')}</span>
            <input
              id="edit-permitted-user-last-name"
              className={style(surfaceStyles.textInput, accessStyles.modalTextInput)}
              type="text"
              placeholder={t('last-name-optional').toString()}
              value={last}
              disabled={saving}
              onChange={(event): void => setLast(event.target.value)}
            />
          </label>

          <span className={style(accessStyles.modalSectionNote)}>{t('edit-user-details-per-office')}</span>

          <label className={style(accessStyles.modalField)} htmlFor="edit-permitted-user-shared-email">
            <span className={style(accessStyles.modalFieldLabel)}>{t('shared-email')}</span>
            <input
              id="edit-permitted-user-shared-email"
              className={style(surfaceStyles.textInput, accessStyles.modalTextInput)}
              type="email"
              maxLength={254}
              placeholder={t('shared-email-optional').toString()}
              value={shared}
              disabled={saving}
              aria-invalid={sharedInvalid}
              onChange={(event): void => setShared(event.target.value)}
            />
            {sharedInvalid && <span className={style(surfaceStyles.fieldError)}>{t('invalid-email-message')}</span>}
          </label>

          <label className={style(accessStyles.modalField)} htmlFor="edit-permitted-user-phone-number">
            <span className={style(accessStyles.modalFieldLabel)}>{t('phone-number')}</span>
            <input
              id="edit-permitted-user-phone-number"
              className={style(surfaceStyles.textInput, accessStyles.modalTextInput)}
              type="tel"
              maxLength={30}
              placeholder={t('phone-number-optional').toString()}
              value={phone}
              disabled={saving}
              aria-invalid={phoneInvalid}
              onChange={(event): void => setPhone(event.target.value)}
            />
            {phoneInvalid && <span className={style(surfaceStyles.fieldError)}>{t('invalid-phone-number-message')}</span>}
          </label>

          {/* Headed like the fields above and said in words beside the switch: a bare switch at
              the foot of the form gives no clue what it turns on. */}
          <label className={style(accessStyles.modalField)} htmlFor="edit-permitted-user-medic">
            <span className={style(accessStyles.modalFieldLabel)}>{t('company-medic')}</span>
            <Row itemSpacing={8} crossAxisAlignment={CrossAxisAlignment.Center}>
              <Switch
                id="edit-permitted-user-medic"
                checked={medic}
                disabled={saving}
                onChange={(value): void => setMedic(value)}
                onColor={StyleConstants.ORANGE_COLOR}
                onHandleColor={StyleConstants.WHITE_COLOR}
                handleDiameter={styles.switchDiameter}
                uncheckedIcon={false}
                checkedIcon={false}
                boxShadow={styles.switchBoxShadow}
                activeBoxShadow={styles.switchActiveBoxShadow}
                height={styles.switchHeight}
                width={styles.switchWidth}
                aria-label={t('company-medic').toString()}
              />
              <span className={style(accessStyles.modalSwitchText)}>
                {medic ? t('company-medic-on') : t('company-medic-off')}
              </span>
            </Row>
          </label>
        </div>

        {failure != null && <span className={style(styles.error)}>{failure}</span>}

        {saving && <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={saving || (!nameChanged && !detailsChanged) || sharedInvalid || phoneInvalid}
              onClick={(): void => {
                if (medicChanged) {
                  setConfirmingMedic(true);
                  return;
                }

                runSave();
              }}
            >
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>

      <ConfirmMedicChangeModal
        showModal={confirmingMedic}
        makeMedic={medic}
        person={[first.trim(), last.trim()].filter(Boolean).join(' ') || email}
        handleAccept={(): void => {
          setConfirmingMedic(false);
          runSave();
        }}
        handleClose={(): void => setConfirmingMedic(false)}
      />
    </Modal>
  );
};

export default EditPermittedUserModal;
